"use client";

import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Phone, ArrowRight } from "lucide-react";
const faqs = [{
  question: "How long does it take to see actual SEO results?",
  answer: "SEO is a strategic investment for long-term growth. While some local visibility improvements happen within the first few weeks, significant revenue growth and top-tier rankings typically take 4-6 months of consistent optimization. We track every lead so you see the momentum build."
}, {
  question: "Will I own my website and marketing data?",
  answer: "Absolutely. Unlike agencies that hold your assets hostage, Fox River Marketing ensures you retain 100% ownership of your website, Google Business Profile, and all campaign data. We believe in earning your partnership every month through measurable results, not restrictive contracts."
}, {
  question: "How is a consultant different from a standard agency?",
  answer: "A standard agency often provides cookie-cutter packages. As consultants, we provide strategic oversight and personalized attention, building revenue engines tailored specifically to your local market. We focus strictly on ROI and lead generation rather than just digital maintenance."
}, {
  question: "Do you focus on traffic or actual lead generation?",
  answer: "Traffic is a vanity metric; revenue is what matters. Our strategies are built to target high-intent local searchers who are ready to buy. We optimize for conversions—turning website visitors into phone calls and scheduled consultations for your business."
}, {
  question: "Can you help if I have a limited marketing budget?",
  answer: "We specialize in high-impact strategies for small businesses. We'll help you prioritize the specific channels—whether it's Local SEO or targeted Google Ads—that will move the needle fastest for your industry, ensuring every dollar spent is an investment in growth."
}];
export default function FrequentlyAskedQuestions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="44479" id="seo-questions-answered" ref={ref} className={cn("bg-[#F6FAFE] text-[#464646] py-24 relative overflow-hidden", "bg-[linear-gradient(to_right,#00000008_1px,transparent_1px),linear-gradient(to_bottom,#00000008_1px,transparent_1px)] bg-[size:40px_40px]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10", "transition-all duration-700 ease-out")}>
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#000000]/10 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#000000]/10 to-transparent" />

      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-3xl md:text-[34px] font-bold tracking-tight text-[#000000] font-sans">
            Straight Answers to <span className="text-[#FF6600]">Critical Questions</span>
          </h2>
          <p className="text-[#464646] text-sm md:text-base max-w-2xl mx-auto font-sans leading-relaxed">
            Choosing a marketing partner is a major decision for your small business. Here is the truth about our strategic approach and what you can expect from us.
          </p>
        </div>

        <div className="bg-white border-2 border-[#E2E8F0] rounded-none p-6 md:p-8 shadow-md">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} className="border-b-[#E2E8F0] last:border-0 px-2 data-[state=open]:border-l-4 data-[state=open]:border-l-[#FF6600] transition-all duration-200" data-index={i}>
                <AccordionTrigger className="text-left text-lg md:text-[18px] font-semibold text-[#000000] hover:text-[#FF6600] transition-colors py-5 font-sans no-underline hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#464646] text-sm leading-relaxed pb-6 pr-4 md:pr-12 font-sans">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        <div className="mt-12 text-center space-y-6">
          <p className="text-[#000000] font-semibold font-sans">
            Ready to stop guessing and start growing?
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto font-bold text-sm uppercase tracking-wider bg-[#FF6600] hover:bg-[#E65C00] text-white rounded-none transition-colors">
              <Link to="/ContactPage">
                Get Your Free Assessment <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-[#000000] text-[#000000] hover:bg-[#000000] hover:text-white rounded-none transition-colors font-bold text-sm uppercase tracking-wider">
              <Link to="tel:5551234567">
                <Phone className="mr-2 size-4" /> (555) 123-4567
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}